import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, BehaviorSubject } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthData } from '../models/auth-data.model';
import { createAuthConfig } from '../auth-config';
import { AccountContextService } from './account-context.service';

export type AuthSessionState = 'initializing' | 'anonymous' | 'authenticated' | 'refreshing' | 'expired';
type JwtClaims = Record<string, any>;

const usernameClaimNames = [
  'http://promobid.promob.com/identity/security/claims/username',
  'username',
  'preferred_username',
  'email',
  'name'
];

@Injectable()
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private authReadySubject = new BehaviorSubject<boolean>(false);
  private sessionStateSubject = new BehaviorSubject<AuthSessionState>('initializing');
  private loginInProgress = false;

  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  authReady$ = this.authReadySubject.asObservable();
  sessionState$ = this.sessionStateSubject.asObservable();
  authChange = new Subject<boolean>();
  redirectPathKey = 'auth_return_url';

  constructor(
    private oauthService: OAuthService,
    private router: Router,
    private accountContext: AccountContextService) {
    this.configureOAuth();
    this.getErrors();
    setTimeout(() => this.initializeOAuth(), 0);
  }

  get selectedAccount$() {
    return this.accountContext.selectedAccount$;
  }

  get accountChanged$() {
    return this.accountContext.accountChanged$;
  }

  get accountKey(): string {
    return this.accountContext.accountKey;
  }

  get userAccounts(): string[] {
    return this.accountContext.getAccounts();
  }

  get defaultAccount(): string {
    return this.accountContext.getDefaultAccountId();
  }

  login(returnUrl: string = this.getCurrentReturnUrl()) {
    const safeReturnUrl = this.getSafeReturnUrl(returnUrl);

    if (safeReturnUrl) {
      sessionStorage.setItem(this.redirectPathKey, safeReturnUrl);
    }

    if (this.loginInProgress) {
      return;
    }

    this.loginInProgress = true;
    this.oauthService.initCodeFlow();
  }

  logout() {
    const hasIdToken = !!this.oauthService.getIdToken();
    this.markAnonymous('anonymous');

    if (hasIdToken) {
      this.oauthService.logOut();
      return;
    }

    this.clearAuthStorage();
    this.router.navigate(['/']);
  }

  completeLogout() {
    this.markAnonymous('anonymous');
    this.clearAuthStorage();
    sessionStorage.removeItem(this.redirectPathKey);
  }

  getAuthData(): AuthData | null {
    if (this.oauthService && this.getAvailableClaims().length > 0) {
      this.initializeAccountState();

      let auth = new AuthData();
      auth.username = this.getUsername();
      auth.accountID = this.getSelectedAccountId() || this.getDefaultAccountId();
      auth.accounts = this.getAccounts();
      auth.token = this.getAccessToken();

      return auth;
    }

    return null;
  }

  isAuthenticated(): boolean {
    const authenticated = this.hasAuthenticatedSession();

    if (authenticated && !this.isAuthenticatedSubject.value) {
      this.markAuthenticated();
    } else if (!authenticated && this.isAuthenticatedSubject.value) {
      this.markAnonymous('anonymous');
    }

    return authenticated;
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  canRefreshToken(): boolean {
    return !!this.oauthService.getRefreshToken();
  }

  refreshToken(): Promise<boolean> {
    if (!this.canRefreshToken()) {
      return Promise.resolve(false);
    }

    this.sessionStateSubject.next('refreshing');

    return this.oauthService.refreshToken()
      .then(() => {
        const authenticated = this.hasAuthenticatedSession();
        if (authenticated) {
          this.initializeAccountState();
          this.markAuthenticated();
        } else {
          this.markAnonymous('expired');
        }
        return authenticated;
      })
      .catch(() => {
        this.markAnonymous('expired');
        return false;
      });
  }

  expireSession(returnUrl: string = this.getCurrentReturnUrl()) {
    this.markAnonymous('expired');
    this.clearAuthStorage();
    this.login(returnUrl);
  }

  setSelectedAccountId(id: string): boolean {
    return this.accountContext.setSelectedAccountId(id);
  }

  getSelectedAccountId(): string {
    return this.accountContext.getSelectedAccountId();
  }

  getDefaultAccountId(): string {
    return this.accountContext.getDefaultAccountId();
  }

  getAccounts(): string[] {
    return this.accountContext.getAccounts();
  }

  getErrors() {
    this.oauthService.events.subscribe(event => {
      if (event.type === 'token_received') {
        if (this.hasAuthenticatedSession()) {
          this.initializeAccountState();
          this.markAuthenticated();
        }
      }

      if (
        event.type === 'token_error' ||
        event.type === 'code_error' ||
        event.type === 'discovery_document_load_error' ||
        event.type === 'token_refresh_error' ||
        event.type === 'silent_refresh_error' ||
        event.type === 'silent_refresh_timeout'
      ) {
        console.error('OAuth Error:', event);
        this.markAnonymous(event.type === 'discovery_document_load_error' ? 'anonymous' : 'expired');
      }
    });
  }

  private configureOAuth() {
    this.oauthService.configure(createAuthConfig());
  }

  private initializeOAuth() {
    this.sessionStateSubject.next('initializing');

    this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        if (this.hasAuthenticatedSession()) {
          this.initializeAccountState();
          this.markAuthenticated();
        } else {
          if (this.oauthService.getAccessToken()) {
            this.clearAuthStorage();
            this.markAnonymous('expired');
            return;
          }

          this.markAnonymous('anonymous');
        }
      })
      .catch(event => {
        console.error('OAuth initialization error:', event);
        this.markAnonymous('anonymous');
      })
      .finally(() => this.authReadySubject.next(true));
  }

  private initializeAccountState() {
    this.accountContext.initializeFromClaims(this.getAvailableClaims());
  }

  private markAuthenticated() {
    this.loginInProgress = false;
    this.sessionStateSubject.next('authenticated');
    this.isAuthenticatedSubject.next(true);
    this.authChange.next(true);
  }

  private markAnonymous(state: AuthSessionState) {
    this.loginInProgress = false;
    this.sessionStateSubject.next(state);
    this.isAuthenticatedSubject.next(false);
    this.authChange.next(false);
    this.accountContext.clear();
  }

  private clearAuthStorage() {
    [
      'access_token',
      'id_token',
      'refresh_token',
      'expires_at',
      'id_token_expires_at',
      'id_token_claims_obj',
      'id_token_stored_at',
      'access_token_stored_at',
      'refresh_token_stored_at',
      'granted_scopes',
      'session_state',
      'nonce',
      'PKCE_verifier',
      'discovery_document',
      'oauth-state',
      'code_verifier'
    ].forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    this.accountContext.clear();
  }

  private getCurrentReturnUrl(): string {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  private hasAuthenticatedSession(): boolean {
    return this.oauthService.hasValidAccessToken() && !!this.oauthService.getIdentityClaims();
  }

  private getUsername(): string {
    const usernames = this.normalizeStringValues(this.getClaimValues(usernameClaimNames));

    return usernames[0] || "";
  }

  private getClaimValues(claimNames: string[]): any[] {
    const values = [];

    this.getAvailableClaims().forEach(claims => {
      claimNames.forEach(claimName => {
        if (claims[claimName] !== undefined && claims[claimName] !== null) {
          values.push(claims[claimName]);
        }
      });
    });

    return values;
  }

  private getAvailableClaims(): JwtClaims[] {
    const claims = [];
    const identityClaims = this.oauthService.getIdentityClaims() as JwtClaims;
    const accessTokenClaims = this.decodeJwtPayload(this.oauthService.getAccessToken());

    if (identityClaims) {
      claims.push(identityClaims);
    }

    if (accessTokenClaims) {
      claims.push(accessTokenClaims);
    }

    return claims;
  }

  private decodeJwtPayload(token: string): JwtClaims | null {
    if (!token) {
      return null;
    }

    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    try {
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
      const decoded = atob(paddedBase64);

      return JSON.parse(decodeURIComponent(decoded.split('').map(char => {
        return `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`;
      }).join('')));
    } catch {
      return null;
    }
  }

  private normalizeStringValues(values: any[]): string[] {
    const normalizedValues = [];

    const addValue = (value: any) => {
      if (value === undefined || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(addValue);
        return;
      }

      if (typeof value === 'object') {
        addValue(value.AccountID ?? value.accountID ?? value.accountId ?? value.account_id ?? value.id ?? value.ID ?? value.value);
        return;
      }

      const stringValue = String(value).trim();
      if (!stringValue) {
        return;
      }

      if (stringValue.startsWith('[') || stringValue.startsWith('{')) {
        try {
          addValue(JSON.parse(stringValue));
          return;
        } catch {
        }
      }

      stringValue.split(',')
        .map(item => item.trim())
        .filter(item => !!item)
        .forEach(item => normalizedValues.push(item));
    };

    values.forEach(addValue);

    return normalizedValues;
  }

  private getSafeReturnUrl(returnUrl: string): string {
    if (!returnUrl || !returnUrl.startsWith('/')) {
      return '/projects';
    }

    const path = returnUrl.split('?')[0].split('#')[0];
    if (path === '/authenticate' || path === '/logout') {
      return '/projects';
    }

    return returnUrl;
  }
}
